// src/pages/NotairesLanding.jsx
// Landing campagne Notaires — « ARTKIV Transmission Professionnelle » (stratégie §22).
// Ton sobre/pro (différent de la landing collectionneur). FR-first (marché France).
// Formulaire lead → public.professional_leads (capture en base ; envoi checklist
// différé jusqu'au rétablissement de l'email). Aucune dépendance Stripe/email.
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { track, getAnonId } from "@/lib/track";
import { useDemoPage } from "@/lib/useDemoPage";
import { BRAND_NAME } from "@/config/brand";
import BrandMark from "@/components/BrandMark";

const PILLARS = [
  { t: "La famille documente", d: "Le collectionneur, les héritiers ou leur mandataire ajoutent progressivement œuvres, photographies, documents, informations d'acquisition, localisations et valeurs disponibles." },
  { t: `${BRAND_NAME} structure`, d: "La plateforme rattache chaque information à la bonne œuvre, mesure la complétude, signale les pièces manquantes et prépare les rapports." },
  { t: "Le notaire organise", d: "Le notaire consulte les éléments partagés, suit l'état du dossier et détermine les interventions nécessaires." },
  { t: "Le professionnel valide", d: "Le commissaire-priseur ou l'expert mandaté contrôle, corrige, estime, précise sa méthode, ajoute ses réserves, date et signe sa validation." },
];

const NOTAIRE_SEES = [
  "les dossiers actifs", "le nombre d'œuvres recensées", "le taux de complétude",
  "les documents présents ou manquants", "les œuvres nécessitant une expertise",
  "les validations en attente", "les compléments demandés", "les rapports disponibles",
];

const EXPERT_RECEIVES = [
  "photographies", "artiste et titre déclarés", "dimensions", "technique", "facture",
  "certificat", "provenance disponible", "localisation", "état de conservation",
  "valeurs antérieures", "observations de la famille",
];

const PRICING = [
  { name: "Compte Notaire Prescripteur", price: "Gratuit", note: "Créer son compte, inviter un client, suivre un dossier pilote." },
  { name: "Dossier Essentiel", price: "290 € HT", note: "Jusqu'à 30 œuvres, suivi de complétude, rapport préparatoire, accès notaire et validateur.", payer: true },
  { name: "Dossier Transmission", price: "590 € HT", note: "Jusqu'à 150 œuvres, workflow de validation, export PDF/Excel, collaboration famille-notaire-expert.", featured: true, payer: true },
  { name: "Collection complexe", price: "dès 1 200 € HT", note: "Plusieurs résidences, indivision/démembrement, nombreux intervenants, accompagnement renforcé.", payer: true },
];

const FAQ = [
  [`${BRAND_NAME} remplace-t-il l'inventaire du commissaire-priseur ?`, `Non. ${BRAND_NAME} prépare les informations et facilite l'intervention du professionnel mandaté.`],
  [`${BRAND_NAME} fournit-il une valeur officielle ?`, "Non. Les valeurs déclarées ou indicatives sont clairement distinguées des valeurs validées par un professionnel."],
  ["Le notaire doit-il saisir les œuvres ?", "Non. La saisie peut être réalisée par la famille, un mandataire, un assistant ou un art advisor."],
  ["Qui paie ?", "Le compte prescripteur du notaire est gratuit. Le dossier opérationnel est activé sur devis, puis réglé par le collectionneur, la famille, la succession ou son mandataire."],
  ["Peut-on inviter notre commissaire-priseur habituel ?", `Oui. ${BRAND_NAME} privilégie les relations professionnelles existantes.`],
  ["Toutes les informations sont-elles visibles par tous ?", "Non. Les accès sont différenciés et accordés explicitement, œuvre par œuvre si besoin."],
  [`${BRAND_NAME} donne-t-il des conseils juridiques ou fiscaux ?`, "Non. La plateforme structure les données et facilite la collaboration."],
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export default function NotairesLanding() {
  useDemoPage("notaires");
  const [form, setForm] = useState({ first_name: "", last_name: "", etude: "", email: "", fonction: "", dossiers_par_an: "", demande: "checklist", message: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);
  const contactRef = useRef(null);

  useEffect(() => {
    track("notaires_landing_view");
    document.title = `${BRAND_NAME} Transmission Professionnelle — pour les notaires et experts`;
  }, []);

  const goContact = (demande) => {
    if (demande) setForm((f) => ({ ...f, demande }));
    requestAnimationFrame(() => contactRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!EMAIL_RE.test(form.email.trim())) { setError("Merci d'indiquer un email professionnel valide."); return; }
    setSending(true); setError(null);
    try {
      // L'edge function enregistre le lead ET envoie l'email (checklist / accusé)
      // via Resend, avec copie à l'équipe. On ne fait plus l'insert côté client.
      const { data, error: err } = await supabase.functions.invoke("notaire-lead", {
        body: {
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
          etude: form.etude.trim(),
          email: form.email.trim().toLowerCase(),
          fonction: form.fonction.trim(),
          dossiers_par_an: form.dossiers_par_an,
          demande: form.demande,
          message: form.message.trim(),
          brand: BRAND_NAME, // adapte l'email (marque, liens, PDF) au site d'origine
          anon_id: getAnonId(),
        },
      });
      if (err || !(data && data.ok)) throw err || new Error("lead_failed");
      track("notaire_lead", { meta: { demande: form.demande } });
      setDone(true);
    } catch (err) {
      console.error("[notaires] lead:", err);
      setError("Un problème est survenu. Réessayez dans un instant.");
    } finally {
      setSending(false);
    }
  };

  const field = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="min-h-screen bg-[#f9f7f4] text-[#1a1a1a] font-serif" style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}>
      {/* NAV */}
      <header className="sticky top-0 z-30 bg-[#f9f7f4]/95 backdrop-blur border-b border-[#e5e0d8]">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <BrandMark className="text-2xl tracking-[0.15em]" />
          <div className="flex items-center gap-4">
            <a href="#workflow" className="hidden md:inline text-sm text-[#6b6459] hover:text-black">Le workflow</a>
            <Link to="/cercle-experts" className="hidden md:inline text-sm text-[#6b6459] hover:text-black">Experts</Link>
            <a href="#tarifs" className="hidden md:inline text-sm text-[#6b6459] hover:text-black">Tarifs</a>
            <button onClick={() => goContact("demo")} className="text-sm px-4 py-2 bg-[#1a1a1a] text-[#f9f7f4] rounded-md hover:bg-black">
              Pour mon étude
            </button>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="max-w-6xl mx-auto px-6 pt-12 md:pt-16 pb-14">
        <div className="grid lg:grid-cols-[1.02fr_0.98fr] gap-10 lg:gap-14 items-center">
          <div>
            <div className="text-xs tracking-[0.18em] uppercase text-[#8a8378] mb-5">{BRAND_NAME} Transmission Professionnelle</div>
            <h1 className="text-4xl md:text-5xl font-bold leading-[1.1] max-w-3xl">
              La collection est dispersée.<br />Le dossier ne devrait pas l'être.
            </h1>
            <p className="mt-6 text-lg text-[#4a453d] leading-relaxed max-w-2xl">
              Lors d'une succession ou d'une donation, {BRAND_NAME} aide les familles à réunir et structurer les informations relatives aux œuvres d'art. Le notaire dispose d'un dossier préparatoire plus clair ; le commissaire-priseur ou l'expert mandaté contrôle, complète et valide plus efficacement.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <button onClick={() => goContact("demo")} className="px-6 py-3 bg-[#1a1a1a] text-[#f9f7f4] rounded-md text-base hover:bg-black">
                Ouvrir gratuitement le compte de mon étude
              </button>
              <button onClick={() => goContact("checklist")} className="px-6 py-3 border border-[#1a1a1a] rounded-md text-base hover:bg-[#1a1a1a] hover:text-[#f9f7f4] transition-colors">
                Télécharger la checklist du dossier art
              </button>
            </div>
            <p className="mt-4 text-sm">
              <Link
                to="/matching-intergenerationnel/demo"
                onClick={() => track("demo_cta", { meta: { route: "/notaires", scenario: "notaires", cta: "voir_cote_famille" } })}
                className="underline text-[#4a453d] hover:text-black"
              >
                Voir l'expérience côté famille →
              </Link>
            </p>
            <p className="mt-4 text-sm text-[#8a8378]">Compte professionnel gratuit. Premier dossier pilote offert aux études partenaires.</p>
          </div>
          <figure className="relative">
            <div className="absolute -inset-3 border border-[#d7ccb5] translate-x-2 translate-y-2" aria-hidden="true" />
            <img
              src="/images/notaires/notaires-hero-dossier.jpg"
              alt="Une notaire examine un dossier d'œuvres avec une famille"
              className="relative w-full aspect-[3/2] object-cover border border-[#ded8cf]"
            />
            <figcaption className="relative mt-3 text-xs leading-relaxed text-[#6b6459]">
              La famille apporte les éléments disponibles. L'étude suit un dossier structuré. Le professionnel mandaté conserve son rôle de validation.
            </figcaption>
          </figure>
        </div>
      </section>

      {/* PHRASE DE CONFIANCE */}
      <section className="border-y border-[#e5e0d8] bg-white">
        <div className="max-w-4xl mx-auto px-6 py-10 text-center">
          <p className="text-xl leading-relaxed text-[#2a261f]">
            {BRAND_NAME} ne remplace ni le notaire, ni le commissaire-priseur, ni l'expert. La plateforme prépare les informations et fluidifie leur intervention.
          </p>
        </div>
      </section>

      {/* PROBLÈME */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="grid lg:grid-cols-[0.95fr_1.05fr] gap-10 lg:gap-14 items-center">
          <div>
            <h2 className="text-3xl font-bold mb-4">Combien de temps faut-il pour reconstituer une collection ?</h2>
            <p className="text-[#4a453d] leading-relaxed mb-8">
              Lorsqu'un dossier comporte des œuvres d'art, les informations sont souvent réparties entre photographies personnelles, factures anciennes, certificats séparés des œuvres, emails, plusieurs résidences, inventaires partiels, estimations obsolètes — et la mémoire d'un seul membre de la famille.
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
              {["ce qui existe", "où les œuvres se trouvent", "quels documents leur correspondent", "quand elles ont été valorisées", "quelles œuvres nécessitent un spécialiste"].map((x) => (
                <div key={x} className="flex items-start gap-3 bg-white border border-[#e5e0d8] px-4 py-3">
                  <span className="text-[#c9a961] mt-0.5">◆</span>
                  <span className="text-sm text-[#4a453d]">Comprendre {x}</span>
                </div>
              ))}
            </div>
            <p className="mt-8 text-lg font-semibold text-[#2a261f]">
              {BRAND_NAME} transforme cette matière dispersée en un dossier structuré, lisible et traçable.
            </p>
          </div>
          <figure>
            <img
              src="/images/notaires/notaires-informations-dispersees.jpg"
              alt="Photographies d'œuvres, documents anciens et téléphone réunis pour préparer un dossier"
              className="w-full aspect-[3/2] object-cover border border-[#ded8cf]"
              loading="lazy"
            />
            <figcaption className="mt-3 text-xs leading-relaxed text-[#6b6459]">
              Photos personnelles, documents scannés, anciennes estimations et localisations : {BRAND_NAME} part de la réalité du dossier, même lorsqu'elle est incomplète.
            </figcaption>
          </figure>
        </div>
      </section>

      {/* WORKFLOW */}
      <section id="workflow" className="bg-white border-y border-[#e5e0d8]">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <h2 className="text-3xl font-bold mb-10">Une chaîne de travail claire</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {PILLARS.map((p, i) => (
              <div key={p.t} className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#1a1a1a] text-[#f9f7f4] flex items-center justify-center font-bold">{i + 1}</div>
                <div>
                  <h3 className="text-lg font-bold mb-1">{p.t}</h3>
                  <p className="text-sm text-[#4a453d] leading-relaxed">{p.d}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-10 text-center text-lg font-semibold text-[#2a261f]">
            La famille documente. {BRAND_NAME} structure. Le notaire organise. Le professionnel valide.
          </p>
          <figure className="mt-12 grid md:grid-cols-[1.2fr_0.8fr] bg-[#f9f7f4] border border-[#e5e0d8]">
            <img
              src="/images/notaires/notaires-validation-professionnelle.jpg"
              alt="Une notaire et un professionnel de l'art contrôlent ensemble un dossier"
              className="w-full h-full min-h-[260px] object-cover"
              loading="lazy"
            />
            <figcaption className="p-7 md:p-9 flex flex-col justify-center">
              <span className="text-xs tracking-[0.16em] uppercase text-[#8a8378] mb-3">Chacun reste dans son rôle</span>
              <strong className="text-2xl leading-tight text-[#2a261f] mb-4">Un dossier préparé n'est pas une expertise automatisée.</strong>
              <span className="text-sm leading-relaxed text-[#4a453d]">
                Le notaire coordonne le dossier. Le commissaire-priseur ou l'expert contrôle les informations, précise sa méthode, formule ses réserves et valide son intervention.
              </span>
            </figcaption>
          </figure>
        </div>
      </section>

      {/* NOTAIRE VOIT / EXPERT REÇOIT */}
      <section className="max-w-6xl mx-auto px-6 py-16 grid md:grid-cols-2 gap-10">
        <div>
          <h2 className="text-2xl font-bold mb-2">Ce que voit le notaire</h2>
          <p className="text-sm text-[#8a8378] mb-5">Un tableau de bord, pas un nouvel inventaire à saisir.</p>
          <ul className="space-y-2">
            {NOTAIRE_SEES.map((x) => (
              <li key={x} className="flex items-start gap-3 text-sm text-[#4a453d]"><span className="text-[#c9a961] mt-0.5">◆</span>{x}</li>
            ))}
          </ul>
        </div>
        <div>
          <h2 className="text-2xl font-bold mb-2">Ce que reçoit le professionnel</h2>
          <p className="text-sm text-[#8a8378] mb-5">Commencer l'expertise au bon endroit.</p>
          <ul className="space-y-2 sm:columns-2">
            {EXPERT_RECEIVES.map((x) => (
              <li key={x} className="flex items-start gap-3 text-sm text-[#4a453d] mb-2 break-inside-avoid"><span className="text-[#c9a961] mt-0.5">◆</span>{x}</li>
            ))}
          </ul>
          <p className="mt-4 text-sm italic text-[#6b6459]">
            Recevoir un dossier préparé ne remplace pas l'expertise. Cela permet de consacrer davantage de temps à l'expertise elle-même.
          </p>
        </div>
      </section>

      {/* TARIFS */}
      <section id="tarifs" className="bg-white border-y border-[#e5e0d8]">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <h2 className="text-3xl font-bold mb-2">Gratuit pour prescrire. Payant pour opérer un dossier réel.</h2>
          <p className="text-sm text-[#8a8378] mb-10">Le compte de l'étude est gratuit. Chaque dossier est activé sur devis, établi avec la famille avant toute facturation.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {PRICING.map((p) => (
              <div key={p.name} className={`rounded-2xl border p-6 flex flex-col ${p.featured ? "border-[#c9a961] bg-[#faf5e9] shadow-sm" : "border-[#e5e0d8] bg-[#f9f7f4]"}`}>
                <div className="text-sm font-semibold text-[#4a453d] mb-1">{p.name}</div>
                <div className="text-2xl font-bold mb-3">{p.price}</div>
                <p className="text-xs text-[#6b6459] leading-relaxed">{p.note}</p>
                {p.payer && (
                  <p className="mt-auto pt-4 text-xs font-semibold text-[#8a6a2f] leading-relaxed">
                    Facturé au collectionneur, à la famille, à la succession ou à son mandataire.
                  </p>
                )}
              </div>
            ))}
          </div>
          <p className="mt-6 text-xs text-[#8a8378] max-w-3xl">
            Les honoraires du commissaire-priseur, de l'expert, du notaire et des autres professionnels sont indépendants et ne sont pas compris dans le tarif {BRAND_NAME}.
          </p>
        </div>
      </section>

      {/* SÉCURITÉ */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <h2 className="text-3xl font-bold mb-8">Sécurité et confidentialité</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {["Collection privée par défaut", "Accès accordés explicitement", "Permissions différenciées", "Partage limité aux œuvres concernées", "Traçabilité des consultations", "Révocation des accès", "Séparation entre dossiers et familles", "Conformité RGPD", "Hébergement européen"].map((x) => (
            <div key={x} className="flex items-start gap-3 bg-white border border-[#e5e0d8] rounded-lg px-4 py-3 text-sm text-[#4a453d]">
              <span className="text-[#c9a961] mt-0.5">✓</span>{x}
            </div>
          ))}
        </div>
        <p className="mt-6 text-sm italic text-[#6b6459] max-w-3xl">
          Le rôle ou le titre professionnel ne donne jamais accès automatiquement à une collection. L'accès résulte toujours d'une autorisation ou d'un mandat.
        </p>
      </section>

      {/* FAQ */}
      <section className="bg-white border-y border-[#e5e0d8]">
        <div className="max-w-3xl mx-auto px-6 py-16">
          <h2 className="text-3xl font-bold mb-8">Questions fréquentes</h2>
          <div className="divide-y divide-[#e5e0d8]">
            {FAQ.map(([q, a]) => (
              <div key={q} className="py-5">
                <h3 className="font-bold text-[#2a261f] mb-1">{q}</h3>
                <p className="text-sm text-[#4a453d] leading-relaxed">{a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FORMULAIRE LEAD */}
      <section ref={contactRef} id="contact" className="max-w-3xl mx-auto px-6 py-16">
        <h2 className="text-3xl font-bold mb-2">Préparer le dossier art d'une succession</h2>
        <p className="text-[#4a453d] mb-8">
          Recevez la checklist, demandez une démonstration ou proposez un premier dossier pilote pour votre étude.
        </p>

        {done ? (
          <div className="bg-[#eef5f1] border border-[#cfe3d8] rounded-xl p-8 text-center">
            <div className="text-2xl mb-2">✓</div>
            <h3 className="text-lg font-bold mb-1">Demande enregistrée</h3>
            <p className="text-sm text-[#4a453d]">Merci. Vous allez recevoir un email à l'adresse indiquée (pensez à vérifier vos spams si besoin). Un membre de l'équipe {BRAND_NAME} revient vers vous pour la suite.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-white border border-[#e5e0d8] rounded-2xl p-6 md:p-8 grid gap-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <input value={form.first_name} onChange={field("first_name")} placeholder="Prénom" className="px-4 py-3 border border-[#e5e0d8] rounded-lg text-sm bg-[#f9f7f4]" />
              <input value={form.last_name} onChange={field("last_name")} placeholder="Nom" className="px-4 py-3 border border-[#e5e0d8] rounded-lg text-sm bg-[#f9f7f4]" />
            </div>
            <input value={form.etude} onChange={field("etude")} placeholder="Étude / cabinet" className="px-4 py-3 border border-[#e5e0d8] rounded-lg text-sm bg-[#f9f7f4]" />
            <input type="email" required value={form.email} onChange={field("email")} placeholder="Email professionnel *" className="px-4 py-3 border border-[#e5e0d8] rounded-lg text-sm bg-[#f9f7f4]" />
            <div className="grid sm:grid-cols-2 gap-4">
              <input value={form.fonction} onChange={field("fonction")} placeholder="Fonction" className="px-4 py-3 border border-[#e5e0d8] rounded-lg text-sm bg-[#f9f7f4]" />
              <select value={form.dossiers_par_an} onChange={field("dossiers_par_an")} className="px-4 py-3 border border-[#e5e0d8] rounded-lg text-sm bg-[#f9f7f4] text-[#4a453d]">
                <option value="">Dossiers art / an (approx.)</option>
                <option value="0-2">0 à 2</option>
                <option value="3-10">3 à 10</option>
                <option value="10+">Plus de 10</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-[#8a8378] mb-2">Votre demande</label>
              <div className="flex flex-wrap gap-2">
                {[["checklist", "Recevoir la checklist"], ["demo", "Demander une démonstration"], ["pilote", "Proposer un dossier pilote"]].map(([v, l]) => (
                  <button type="button" key={v} onClick={() => setForm((f) => ({ ...f, demande: v }))}
                    className={`px-4 py-2 rounded-lg text-sm border transition-colors ${form.demande === v ? "bg-[#1a1a1a] text-[#f9f7f4] border-[#1a1a1a]" : "border-[#e5e0d8] text-[#4a453d] hover:bg-[#f9f7f4]"}`}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
            <textarea value={form.message} onChange={field("message")} placeholder="Message (optionnel)" rows={3} className="px-4 py-3 border border-[#e5e0d8] rounded-lg text-sm bg-[#f9f7f4]" />
            {error && <p className="text-sm text-[#dc2626]">{error}</p>}
            <button type="submit" disabled={sending} className="px-6 py-3 bg-[#c9a961] text-[#1a1a1a] font-semibold rounded-lg hover:bg-[#b9975a] disabled:opacity-60">
              {sending ? "Envoi…" : "Envoyer ma demande"}
            </button>
            <p className="text-xs text-[#8a8378]">
              Compte professionnel gratuit. Premier dossier pilote offert aux études partenaires. Vos données restent privées — voir notre{" "}
              <Link to="/privacy-policy" className="underline">politique de confidentialité</Link>.
            </p>
          </form>
        )}
      </section>

      {/* CTA FINAL */}
      <section className="bg-[#1a1a1a] text-[#f9f7f4]">
        <div className="max-w-4xl mx-auto px-6 py-16 text-center">
          <h2 className="text-3xl font-bold mb-4">Une collection mérite mieux qu'un dossier reconstitué dans l'urgence</h2>
          <p className="text-[#d8d2c6] leading-relaxed max-w-2xl mx-auto mb-8">
            Préparez les informations en amont, organisez l'intervention des professionnels et disposez d'un dossier plus clair pour la transmission.
          </p>
          <button onClick={() => goContact("demo")} className="px-7 py-3 bg-[#c9a961] text-[#1a1a1a] font-semibold rounded-md hover:bg-[#b9975a]">
            Demander une démonstration
          </button>
          <p className="mt-8 text-sm tracking-wide text-[#c9a961]">{BRAND_NAME} prépare. Les professionnels valident. La transmission avance.</p>
        </div>
      </section>

      <footer className="max-w-6xl mx-auto px-6 py-10 text-xs text-[#8a8378] flex flex-wrap gap-4 justify-between">
        <span>© {new Date().getFullYear()} {BRAND_NAME} — Transmission Professionnelle</span>
        <span className="flex gap-4">
          <Link to="/mentions-legales" className="hover:text-black">Mentions légales</Link>
          <Link to="/privacy-policy" className="hover:text-black">Confidentialité</Link>
        </span>
      </footer>
    </div>
  );
}
